// import { useState, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { useState, forwardRef, useImperativeHandle, useMemo } from "react";
import { decodeSuiPrivateKey } from "@mysten/sui/cryptography";
import {
  Alert,
  Button,
  Form,
  Modal,
  Table,
  TableProps,
  Tag,
  message,
  Image,
  Dropdown,
  MenuProps,
} from "antd";
import ImportWalletKey from "../ImportWalletKey";
import "./index.less";
import { cdnUrl } from "@/utils/cdn";

const UploadModal = ({ onSuccess }: any, ref: any) => {
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [keypairs, setKeypairs] = useState("");
  const keypairValue = Form.useWatch("keypair", form);


  const handleHideModal = () => {
    setOpenModal(false);
  };

  useImperativeHandle(ref, () => ({
    init: () => {
      setOpenModal(true);
    },
  }));

  function onSubmitKeyPair() {
    form
      .validateFields()
      .then((values) => {
        // 表单校验通过，values为表单的值
        console.log("Success:", values);
        const { keypair } = values;
        // 👉 去掉空行 / 全是空格的行
        const cleanedKeypair = (keypair || "")
          .split(/\r?\n/)          // 按行拆
          .map((line: any) => line.trim()) // 去掉每行首尾空格
          .filter((line: any) => line.length > 0) // 过滤掉空行
          .join("\n");

        setKeypairs(cleanedKeypair);
        handleHideModal();
        onSuccess(cleanedKeypair);
        form.setFieldValue("keypair", "");
        // 在这里处理表单提交等逻辑
      })
      .catch((errorInfo) => {
        // 表单校验失败
        console.log("Failed:", errorInfo);
        // 在这里处理校验失败的逻辑，例如显示错误信息
      });
  }

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <a target="_blank" href={cdnUrl("/demo/xlsx-key-demo.xlsx")} download>
          Excel
        </a>
      ),
    },
    {
      key: "2",
      label: (
        <a target="_blank" href={cdnUrl("/demo/csv-key-demo.csv")} download>
          CSV
        </a>
      ),
    },
    {
      key: "3",
      label: (
        <a target="_blank" href={cdnUrl("/demo/txt-key-demo.txt")} download>
          TXT
        </a>
      ),
    },
    {
      key: "4",
      label: (
        <a target="_blank" href={cdnUrl("/demo/json-key-demo.json")} download>
          JSON
        </a>
      ),
    },
  ];
  // 判断一条是否是合法的 EVM 私钥
  // 判断一条是否是合法的 EVM 私钥：0x + 64 hex
  // const isValidPrivateKey = (pk: string): boolean => {
  //   return /^0x[0-9a-fA-F]{64}$/.test(pk);
  // };

  const isValidPrivateKey = (pk: string): boolean => {
    try {
      const trimmed = (pk ?? "").trim();
      if (!trimmed) return false;

      // ✅ 能 decode 就认为合法（支持 suiprivkey1... 等 Sui 标准私钥格式）
      const { schema, secretKey } = decodeSuiPrivateKey(trimmed);

      // schema: 'ED25519' | 'Secp256k1' | 'Secp256r1'
      // secretKey: Uint8Array(32)
      return (
        !!schema &&
        secretKey instanceof Uint8Array &&
        secretKey.length === 32
      );
    } catch {
      return false;
    }
  };

  // 解析整段文本，拆行 + 清洗 + 区分正确/错误/重复
  const parseKeypairs = (raw: string) => {
    const lines = (raw || "").split(/\r?\n/);
    const valid: string[] = [];
    const invalid: { index: number; raw: string; cleaned: string }[] = [];

    // 用来去重：同一个 key 只保留一次
    const seen = new Set<string>();

    lines.forEach((line, idx) => {
      const rawLine = line;
      let cleaned = rawLine.trim();

      if (!cleaned) {
        // 空行直接跳过
        return;
      }

      // Excel 复制出来：0x...<TAB>别的 → 只取第一段
      cleaned = cleaned.split(/\s+/)[0];

      // 干掉所有空白（空格、Tab）
      cleaned = cleaned.replace(/\s+/g, "");

      // 如果没 0x 但像 64 位 hex，就自动补 0x
      if (!cleaned.startsWith("0x") && /^[0-9a-fA-F]{64}$/.test(cleaned)) {
        cleaned = "0x" + cleaned;
      }

      if (!isValidPrivateKey(cleaned)) {
        // 格式不合法
        invalid.push({
          index: idx + 1,
          raw: rawLine,
          cleaned,
        });
        return;
      }

      // 到这里说明格式已经合法了，再检查是不是重复
      const normalized = cleaned.toLowerCase(); // 忽略大小写去重
      if (seen.has(normalized)) {
        // 重复的也算到 invalid 里
        invalid.push({
          index: idx + 1,
          raw: rawLine,
          cleaned,
        });
        return;
      }

      // 首次出现，记到 valid + seen 里
      seen.add(normalized);
      valid.push(cleaned);
    });

    return { valid, invalid };
  };

  const removeInvalidKeypairs = (form: any) => {
    // form.setFieldValue("keypair", 1221)
    const raw: string = form.getFieldValue("keypair") || "";
    // console.log("raw", raw)
    const { valid, invalid } = parseKeypairs(raw);
    // console.log("valid", valid)
    // 把正确的重新写回 textarea，一行一个
    form.setFieldValue("keypair", valid.join("\n"));

    if (invalid.length > 0) {
      // message.warning(
      //   `已自动删除 ${invalid.length} 条格式错误的私钥（行号：${invalid
      //     .map((i) => i.index)
      //     .join(", ")}）`
      // );
    } else {
      // message.success("所有私钥格式均正确，无需清理");
    }
  };
  const invalidCount = useMemo(() => {
    const raw = keypairValue || "";
    const { invalid } = parseKeypairs(raw);
    return invalid.length;
  }, [keypairValue]);

  return (
    <Modal
      wrapClassName="sl-upload-modal"
      title={t("components.importWallet")}
      open={openModal}
      // onOk={handleOk}
      onCancel={handleHideModal}
      okButtonProps={{ disabled: true }}
      cancelButtonProps={{ disabled: true }}
      footer={null}
      width={868}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        style={{ width: "100%" }}
        className="cus-form-box"
        // onFinish={onFinish}
        initialValues={{}}
      >
        <ImportWalletKey form={form} />
        {invalidCount > 0 && (
          <div
            className="privatekey-remove-false"
            onClick={() => {
              removeInvalidKeypairs(form);
            }}
          >
            <div className="dot"></div>
            <div>{t("components.tip2")}</div>
            <div className="delete">{t("components.onDelete")}</div>
          </div>
        )}
        {/* <div className="privatekey-remove-false" onClick={() => { removeInvalidKeypairs(form) }}>
          <div className="dot"></div>

          <div>{t("components.tip2")}</div>

          <div className="delete">{t("components.onDelete")}</div>
        </div> */}
        <Form.Item>
          <div className="form-item-menu-box">
            <div className="demo-box">
              <Dropdown menu={{ items }} trigger={["click"]}>
                <a onClick={(e) => e.preventDefault()}>
                  {t("components.TemplateFile")}
                </a>
              </Dropdown>
            </div>
            <div className="btns">
              <Button type="default" onClick={handleHideModal}>
                {t("btn.cancel")}
              </Button>
              <Button type="primary" onClick={onSubmitKeyPair}>
                {t("btn.confirm")}
              </Button>
            </div>
          </div>
        </Form.Item>
      </Form>

    </Modal>
  );
};

export default forwardRef(UploadModal);